// Lead Leakage Scorecard — canonical scoring logic (pure module, unit-tested via node).
// Scoring contract: every question is worth 0–3 points. All 8 questions contribute EQUALLY
// to the overall score (overall = total points / 24 × 100), regardless of how questions
// group into the six-stage framework. Stage scores average only their own questions.

export const STAGE_ORDER = ["capture", "respond", "qualify", "follow_up", "book", "track"];

export const STAGE_META = {
  capture: {
    label: "Capture",
    recommendation: "Consolidate every inquiry source — website, portals, phone, open-house sign-ins — into one place you actually check, so no buyer or seller lead starts its life scattered.",
    planAction: "List every place a buyer or seller can raise their hand (website form, portals, phone, open-house sign-ins) and make sure each one lands in a single place you check daily.",
  },
  respond: {
    label: "Respond",
    recommendation: "Set a first-response standard of minutes, not hours, and draft two reusable first-response templates (buyer and seller) so a fast reply never depends on you being free.",
    planAction: "Write two reusable first-response templates (buyer and seller) and set your response-time standard for new inquiries, including after hours.",
  },
  qualify: {
    label: "Qualify",
    recommendation: "Define your minimum qualification questions — timeline, financing readiness, motivation — and capture the answers in the same place every time, so you always know who is ready to move.",
    planAction: "Write down your three minimum qualification questions and start asking them in every first conversation.",
  },
  follow_up: {
    label: "Follow Up",
    recommendation: "Put every active lead on a written cadence (for example day 1, 3, 7, 14) and give dormant leads a recurring nurture touch, so follow-up stops depending on memory.",
    planAction: "Define a simple follow-up cadence (day 1, 3, 7, 14) and put every active lead on it — no lead relying on memory.",
  },
  book: {
    label: "Book",
    recommendation: "Give qualified prospects one clear, low-friction way to book the next step — a showing or consultation — and use it in every response.",
    planAction: "Pick one clear way for prospects to schedule a showing or consultation, and use it every time you respond.",
  },
  track: {
    label: "Track",
    recommendation: "Record the outcome of every lead for the next 30 days — contacted, qualified, booked, or dormant — so you can see exactly where opportunities stop moving.",
    planAction: "Record what happened to every lead this week — contacted, qualified, booked, or dormant — so nothing disappears quietly.",
  },
};

export const QUESTIONS = [
  {
    id: "q1", stage: "capture",
    text: "When a new buyer or seller inquiry arrives (website, portal, sign-in sheet), what happens to it?",
    options: [
      "It lands in my CRM automatically",
      "I enter it manually within the day",
      "It sits in email, texts, or paper until I get to it",
      "Honestly, some get lost",
    ],
  },
  {
    id: "q2", stage: "respond",
    text: "How quickly does a new inquiry typically get its first response?",
    options: ["Under 5 minutes", "Within 30 minutes", "Same day", "Next day or later"],
  },
  {
    id: "q3", stage: "respond",
    text: "What happens to inquiries that arrive after hours or on weekends?",
    options: [
      "They get an immediate response, automated or manual",
      "I respond first thing the next morning",
      "They often wait a day or more",
      "They're often missed entirely",
    ],
  },
  {
    id: "q4", stage: "qualify",
    text: "Do you have a consistent set of questions to qualify buyers and sellers (timeline, financing, motivation)?",
    options: [
      "Yes — every new lead answers them",
      "Yes, but I don't always ask",
      "I mostly go by feel",
      "No",
    ],
  },
  {
    id: "q5", stage: "follow_up",
    text: "After first contact, how many follow-up attempts does a lead typically get?",
    options: [
      "5+ touches on a set cadence",
      "3–4 attempts",
      "1–2 attempts",
      "Usually none after the first",
    ],
  },
  {
    id: "q6", stage: "follow_up",
    text: "How do you handle older or dormant leads in your database?",
    options: [
      "A regular nurture campaign runs consistently",
      "Scheduled check-ins — manual, but they happen",
      "Occasional outreach when I remember",
      "They sit untouched",
    ],
  },
  {
    id: "q7", stage: "book",
    text: "How easy is it for a prospect to book a showing or consultation with you?",
    options: [
      "They can self-schedule instantly through a link",
      "A quick back-and-forth, usually same day",
      "It usually takes a few exchanges",
      "There's no set process",
    ],
  },
  {
    id: "q8", stage: "track",
    text: "Can you see, right now, what happened to every lead from the last 30 days?",
    options: [
      "Yes — my records show every outcome",
      "Mostly, with some gaps",
      "I could piece it together manually",
      "No",
    ],
  },
];

const PLAN_FIXED = {
  1: "Map your current lead flow: write down exactly what happens to a new inquiry from the moment it arrives to the booked appointment.",
  5: "Set a weekly 15-minute pipeline review: every lead, its stage, and its next step.",
  6: "Test your own funnel: submit a test inquiry through your website and portal profiles, and time the response.",
  7: "Review the week, note what changed, and decide which one fix becomes permanent.",
};

export const scoreLabel = (s) =>
  s >= 80
    ? "Strong Foundation"
    : s >= 55
    ? "Some Process Gaps"
    : s >= 30
    ? "Significant Improvement Opportunities"
    : "High-Priority Process Gaps";

// pointMap: { q1: 0-3, ..., q8: 0-3 }
export function computeResults(pointMap) {
  const stageScores = {};
  for (const stage of STAGE_ORDER) {
    const qs = QUESTIONS.filter((q) => q.stage === stage);
    const pts = qs.reduce((sum, q) => sum + (pointMap[q.id] ?? 0), 0);
    stageScores[stage] = Math.round((pts / (qs.length * 3)) * 100);
  }
  // Equal weight per question: total points across all 8 questions, out of 24
  const totalPts = QUESTIONS.reduce((sum, q) => sum + (pointMap[q.id] ?? 0), 0);
  const overall = Math.round((totalPts / (QUESTIONS.length * 3)) * 100);
  // Deterministic: stable sort keeps framework order (STAGE_ORDER) on ties
  const topStages = [...STAGE_ORDER].sort((a, b) => stageScores[a] - stageScores[b]).slice(0, 3);
  return { stageScores, overall, topStages };
}

export function buildPlan(topStages) {
  const plan = { ...PLAN_FIXED };
  topStages.slice(0, 3).forEach((k, i) => {
    plan[i + 2] = STAGE_META[k].planAction;
  });
  return Array.from({ length: 7 }, (_, i) => plan[i + 1]);
}
